// React Query wiring for step 7 (pivot or persevere).
//
// Two server reads: the deterministic report (GET /alignment, recomputed from
// the graph on every fetch) and the AI proposal row (GET /alignment/proposal).
// generate overwrites the row. apply changes the graph (a comprises edge, or
// metadata.verdict on an assumption), so it invalidates the report and the
// canvas as well as the row. dismiss touches only the row. reopen removes a
// verdict from a node, so report + canvas. Hand corrections to the mapping use
// the generic edge CRUD, as before: create also refreshes the canvas so a
// follow-up unmap can find the edge id; the report carries comprisesEdgeId so
// removal needs no canvas read.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import type { AlignmentProposalRow, AlignmentReport, ApplyAlignmentInput, SetVerdictInput } from '@/types/api';

const reportKey = (projectId: string) => ['alignment', projectId] as const;
const proposalKey = (projectId: string) => ['alignment-proposal', projectId] as const;

export function useAlignment(projectId: string) {
  return useQuery<AlignmentReport>({
    queryKey: reportKey(projectId),
    queryFn: async () => {
      const res = await api.getAlignment(projectId);
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to load the review');
      return res.data;
    },
    enabled: !!projectId,
  });
}

export function useAlignmentProposal(projectId: string) {
  return useQuery<AlignmentProposalRow | null>({
    queryKey: proposalKey(projectId),
    queryFn: async () => {
      const res = await api.getAlignmentProposal(projectId);
      if (!res.success) throw new Error(res.message || 'Failed to load suggestions');
      return res.data ?? null;
    },
    enabled: !!projectId,
  });
}

export function useGenerateAlignment(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<AlignmentProposalRow, Error, void>({
    mutationFn: async () => {
      const res = await api.generateAlignment(projectId);
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to weigh the evidence');
      return res.data;
    },
    onSuccess: (row) => {
      queryClient.setQueryData(proposalKey(projectId), row);
    },
  });
}

export function useApplyAlignment(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, ApplyAlignmentInput>({
    mutationFn: async (input) => {
      const res = await api.applyAlignment(projectId, input);
      if (!res.success) throw new Error(res.message || 'Failed to save');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: proposalKey(projectId) });
      queryClient.invalidateQueries({ queryKey: reportKey(projectId) });
      queryClient.invalidateQueries({ queryKey: ['canvas', projectId] });
    },
  });
}

export function useDismissAlignment(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (proposalId) => {
      const res = await api.dismissAlignment(projectId, proposalId);
      if (!res.success) throw new Error(res.message || 'Failed to dismiss');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: proposalKey(projectId) });
    },
  });
}

export function useReopenVerdict(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { assumptionId: string; decidedAt?: string }>({
    mutationFn: async ({ assumptionId, decidedAt }) => {
      // decidedAt is the verdict the PM is looking at; the server refuses to
      // remove a different (newer) one.
      const res = await api.reopenVerdict(projectId, assumptionId, decidedAt);
      if (!res.success) throw new Error(res.message || 'Failed to reopen');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKey(projectId) });
      queryClient.invalidateQueries({ queryKey: ['canvas', projectId] });
    },
  });
}

// The PM's own call on an assumption with no AI proposal behind it. Writes
// metadata.verdict like apply does, so the same queries refresh.
export function useSetVerdict(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, SetVerdictInput>({
    mutationFn: async (input) => {
      const res = await api.setVerdict(projectId, input);
      if (!res.success) throw new Error(res.message || 'Failed to save the call');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKey(projectId) });
      queryClient.invalidateQueries({ queryKey: ['canvas', projectId] });
    },
  });
}

export function useSetComprises(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { ideaNodeId: string; capabilityId: string }>({
    mutationFn: async ({ ideaNodeId, capabilityId }) => {
      const res = await api.createEdge({ projectId, sourceNodeId: ideaNodeId, targetNodeId: capabilityId, edgeType: 'comprises' });
      if (!res.success) throw new Error(res.message || 'Failed to update the mapping');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKey(projectId) });
      queryClient.invalidateQueries({ queryKey: ['canvas', projectId] });
    },
  });
}

export function useRemoveComprises(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { edgeId: string }>({
    mutationFn: async ({ edgeId }) => {
      const res = await api.deleteEdge(edgeId, projectId);
      if (!res.success) throw new Error(res.message || 'Failed to update the mapping');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKey(projectId) });
      queryClient.invalidateQueries({ queryKey: ['canvas', projectId] });
    },
  });
}
